import { faCalendar, faClock } from "@fortawesome/free-regular-svg-icons";
import {
  faCheck,
  faLocationDot,
  faMagnifyingGlass,
  faMinus,
  faPenToSquare,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, InputAdornment, Stack, TextField, Tooltip } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getValetTeams } from "../../../redux/Actions/maidAction";
import TipVerificationModal from "./components/TipVerificationModal";

const VerifiedPoolTipsList = ({ canRead, service }) => {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { valetTeams, totalTeams, loading, error } = useSelector(
    (state) => state.valetTeams,
  );

  const dispatch = useDispatch();

  const [teamToVerify, setTeamToVerify] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [show, setShow] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0, // initial page index
    pageSize: 25, // default page size
  });

  const hotelId = userInfo.isAdmin ? userInfo._id : userInfo.hotel;

  const handleClose = () => {
    setShow(false);
    setLocalSuccess(false);
  };

  const getTime = (timestampStr) => {
    return timestampStr ? moment(timestampStr).format("h:mm A") : "N/A";
  };

  const isTimeWithinRange = (teamTime, startTime, endTime) => {
    if (!teamTime || !startTime || !endTime) return true;

    const teamMoment = moment(teamTime, "h:mm A");
    const startMoment = moment(startTime, "HH:mm");
    const endMoment = moment(endTime, "HH:mm");

    return teamMoment.isBetween(startMoment, endMoment, null, "[]");
  };

  const handleFilter = () => {
    try {
      const filtered = valetTeams.filter((team) => {
        const teamStartDate = moment(team.startDate);
        const teamEndDate = moment(team.endDate);
        const teamTime = getTime(team.createdAt); // 12h format

        const isTimeInRange =
          !startTime ||
          !endTime ||
          isTimeWithinRange(teamTime, startTime, endTime);

        const isDateInRange =
          (!startDate || teamStartDate.isSameOrAfter(moment(startDate))) &&
          (!endDate || teamEndDate.isSameOrBefore(moment(endDate)));

        const isLocationMatch =
          !searchQuery ||
          team.location?.toLowerCase().includes(searchQuery.toLowerCase());

        return isDateInRange && isLocationMatch && isTimeInRange;
      });

      setFilteredTeams(filtered);
      setFiltersApplied(true);
    } catch (err) {
      console.error("Filter error:", err);
      setFilteredTeams([]);
      setFiltersApplied(true);
    }
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSearchQuery("");
    setFilteredTeams([]);
    setFiltersApplied(false);
  };

  useEffect(() => {
    if (hotelId !== "" && hotelId !== undefined) {
      dispatch(
        getValetTeams({
          hotelId,
          service,
          page: pagination.pageIndex + 1, // API expects 1-based index
          limit: pagination.pageSize,
          verified: true,
        }),
      );
    }
  }, [dispatch, hotelId, pagination]);

  const data = useMemo(() => {
    const teamsToDisplay = filtersApplied ? filteredTeams : valetTeams;

    return (
      teamsToDisplay
        ?.filter((team) => team.verified)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((team) => ({
          ...team,
          location:
            team.location === "$session.params.location" || !team.location
              ? "Not Defined"
              : team.location,
          createdAtFormatted: team.createdAt
            ? moment(team.createdAt).format("MM/DD/YYYY • h:mm A")
            : "N/A",
          time: getTime(team.createdAt),
          dateRange: (() => {
            const hasStart = team.startDate && moment(team.startDate).isValid();
            const hasEnd = team.endDate && moment(team.endDate).isValid();

            if (hasStart && hasEnd) {
              return `${moment(team.startDate).format("MM/DD/YYYY")} → ${moment(
                team.endDate,
              ).format("MM/DD/YYYY")}`;
            }

            if (hasStart) {
              return `From ${moment(team.startDate).format("MM/DD/YYYY")}`;
            }

            if (hasEnd) {
              return `Until ${moment(team.endDate).format("MM/DD/YYYY")}`;
            }

            return "No Date Provided";
          })(),
          verifiedAt: team.verifiedAt
            ? `${moment(team.verifiedAt).format("MM/DD/YYYY")}, ${getTime(
                team.verifiedAt,
              )}`
            : "N/A",
        })) || []
    );
  }, [filteredTeams, filtersApplied, valetTeams]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "location",
        header: "Location",
        size: 150,
      },
      {
        accessorKey: "createdAtFormatted",
        header: "Created At",
        size: 180,
        Cell: ({ cell }) => (
          <Box className="">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            {cell.getValue()}
          </Box>
        ),
      },
      {
        accessorKey: "dateRange",
        header: "Tip's Date Range",
        size: 200,
      },
      {
        accessorKey: "tip",
        header: "Tip",
        size: 80,
        Cell: ({ cell }) => `$${cell.getValue()}`,
      },
      {
        accessorKey: "review",
        header: "Review",
        size: 200,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => (
          <Button
            disabled={canRead}
            variant={
              row.original.verified ? "outline-success" : "outline-primary"
            }
            onClick={() => {
              setShow(true);
              setTeamToVerify(row.original);
            }}
          >
            <FontAwesomeIcon
              className="me-2"
              icon={row.original.verified ? faCheck : faPenToSquare}
            />
            {row.original.verified ? "Verified" : "Verify"}
          </Button>
        ),
      },
    ],
    [canRead],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: {
      density: "compact",
    },
    state: {
      isLoading: loading,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: totalTeams || 0,
    muiPaginationProps: {
      showFirstButton: true,
      showLastButton: true,
    },
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
  });

  return (
    <>
      {/* Team Verification Modal */}
      <TipVerificationModal
        show={show}
        onHide={handleClose}
        team={teamToVerify}
      />

      <Card border="0" className="custom-shadow rounded-4 mb-4">
        <Card.Body>
          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              marginBottom: 2,
              paddingTop: 1,
              paddingBottom: 1,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              // Responsive layout
              "@media (max-width: 900px)": {
                "& > *": { minWidth: "calc(50% - 16px)" },
              },
              "@media (max-width: 600px)": {
                "& > *": { width: "100%" },
              },
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              slotProps={{
                textField: {
                  variant: "outlined",
                },
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faCalendar} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faCalendar} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faClock} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faClock} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Search Location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Tooltip title="Clear Filters">
              <Button variant="outline-secondary" onClick={clearFilters}>
                <FontAwesomeIcon icon={faRotateLeft} />
              </Button>
            </Tooltip>
            <Tooltip title="Apply Filters">
              <Button onClick={handleFilter}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
            </Tooltip>
          </Box>

          {/* Results */}
          <MaterialReactTable table={table} />
        </Card.Body>
      </Card>
    </>
  );
};

export default VerifiedPoolTipsList;
